// frontend/src/components/masters/TransporterList.js
import React, { useState, useEffect } from 'react';
import TransporterForm from './TransporterForm';
import useSortableTable from '../common/useSortableTable';
import './TransporterList.css';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

// Placeholder API endpoint - replace with your actual Django API endpoint for Transporters
const API_BASE_URL = '/api/masters/transporters/'; // Added trailing slash

// Helper to get auth token
const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const TransporterList = () => {
    const [transporters, setTransporters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTransporter, setCurrentTransporter] = useState(null);
    const [formMode, setFormMode] = useState('add'); // 'add', 'edit', 'view'
    const [searchTerm, setSearchTerm] = useState('');
    const { hasPermission } = usePermissions(); // 2. Get hook

    // Fetch data from Django backend
    const fetchTransporters = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(API_BASE_URL, {
                headers: getAuthHeaders()
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch transporters');
            }
            const data = await response.json();
            // Assign a unique temporary ID if not already present, useful for lists before saving
            const processedData = data.map(item => ({ ...item, id: item.id || crypto.randomUUID() }));
            setTransporters(processedData);
        } catch (err) {
            console.error("Error fetching transporters:", err);
            setError("Failed to load transporters: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransporters();
    }, []);

    const handleAddTransporter = () => {
        setCurrentTransporter(null); // Clear any previous data
        setFormMode('add');
        setIsModalOpen(true);
    };

    const handleEditTransporter = (transporter) => {
        setCurrentTransporter(transporter);
        setFormMode('edit');
        setIsModalOpen(true);
    };

    const handleViewTransporter = (transporter) => {
        setCurrentTransporter(transporter);
        setFormMode('view');
        setIsModalOpen(true);
    };

    const handleDeleteTransporter = async (id) => {
        if (window.confirm("Are you sure you want to delete this transporter?")) { // Use custom modal in real app
            try {
                const response = await fetch(`${API_BASE_URL}${id}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to delete transporter');
                }
                setTransporters(prev => prev.filter(t => t.id !== id));
                // alert("Transporter deleted successfully!"); // Use custom modal for success
            } catch (err) {
                console.error("Error deleting transporter:", err);
                setError("Failed to delete transporter: " + err.message);
            }
        }
    };

    const handleFormClose = (refresh = false) => {
        setIsModalOpen(false);
        setCurrentTransporter(null); // Clear current transporter data
        if (refresh) {
            fetchTransporters(); // Re-fetch data if a save operation occurred
        }
    };

    const getFilteredTransporters = () => {
        return [...transporters]
            .filter(transporter =>
                Object.values(transporter).some(value =>
                    String(value).toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
    };

    const { items: sortedTransporters, requestSort, getSortIndicator } = useSortableTable(getFilteredTransporters());

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div className="transporter-list-container">
            <h2>Transporters List</h2>

            {loading && <p className="loading-message">Loading transporters...</p>}
            {error && <p className="error-message">{error}</p>}

            <div className="toolbar">
                <input
                    type="text"
                    placeholder="Search transporters..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {/* 3. Protect Add button */}
                {hasPermission('TRANSPORTERS', 'can_create') && (
                    <button onClick={handleAddTransporter}>Add New Transporter</button>
                )}
            </div>

            {!loading && !error && (
                sortedTransporters.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('name')} className="sortable">
                                    Name {getSortIndicator('name')}
                                </th>
                                <th onClick={() => requestSort('phone')} className="sortable">
                                    Phone {getSortIndicator('phone')}
                                </th>
                                <th onClick={() => requestSort('email')} className="sortable">
                                    Email {getSortIndicator('email')}
                                </th>
                                <th onClick={() => requestSort('bank')} className="sortable">
                                    Bank {getSortIndicator('bank')}
                                </th>
                                <th onClick={() => requestSort('account_number')} className="sortable">
                                    Account Number {getSortIndicator('account_number')}
                                </th>
                                {/* 4. Protect Actions column header */}
                                {(hasPermission('TRANSPORTERS', 'can_view') || hasPermission('TRANSPORTERS', 'can_edit') || hasPermission('TRANSPORTERS', 'can_delete')) && (
                                    <th>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTransporters.map(transporter => (
                                <tr key={transporter.id}>
                                    <td>{transporter.name}</td>
                                    <td>{transporter.phone}</td>
                                    <td>{transporter.email}</td>
                                    <td>{transporter.bank_name}</td>
                                    <td>{transporter.account_number}</td>
                                    {/* 5. Protect Actions cell */}
                                    {(hasPermission('TRANSPORTERS', 'can_view') || hasPermission('TRANSPORTERS', 'can_edit') || hasPermission('TRANSPORTERS', 'can_delete')) && (
                                        <td className="actions">
                                            {hasPermission('TRANSPORTERS', 'can_view') && (
                                                <button className="icon-btn view-btn" onClick={() => handleViewTransporter(transporter)} title="View">
                                                    <i className="fa fa-eye"></i>
                                                </button>
                                            )}
                                            {hasPermission('TRANSPORTERS', 'can_edit') && (
                                                <button className="icon-btn edit-btn" onClick={() => handleEditTransporter(transporter)} title="Edit">
                                                    <i className="fa fa-pencil-alt"></i>
                                                </button>
                                            )}
                                            {hasPermission('TRANSPORTERS', 'can_delete') && (
                                                <button className="icon-btn delete-btn" onClick={() => handleDeleteTransporter(transporter.id)} title="Delete">
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className="no-data">No transporters found.</p>
                )
            )}

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
                            &times;
                        </button>
                        <TransporterForm
                            mode={formMode}
                            initialData={currentTransporter}
                            onClose={handleFormClose}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TransporterList;
